import { createContext, use, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@kognitos/lattice';

import { StatusDot } from '@/shared/components/StatusDot';

import type { TBrowserPreviewStatus } from '../types';

/**
 * Lets a host that draws its own title take the minimal browser preview's
 * Live/Replay status and show it there, instead of over the video.
 */
// eslint-disable-next-line no-restricted-syntax -- reason: scoped per card; a store would need every host to key its own entry
export const BrowserPreviewStatusContext = createContext<
  ((status: TBrowserPreviewStatus) => void) | null
>(null);

/**
 * Reports `status` to the nearest host, clearing it on unmount.
 *
 * @param status - The preview's current status
 * @returns Whether a host takes the status, so the preview can skip its own badge
 */
export function useReportBrowserPreviewStatus(
  status: TBrowserPreviewStatus,
): boolean {
  const report = use(BrowserPreviewStatusContext);
  // eslint-disable-next-line no-restricted-syntax -- reason: pushes state up to a host outside this subtree, with cleanup
  useEffect(() => {
    if (!report) return;
    report(status);
    return () => report(null);
  }, [report, status]);
  return report !== null;
}

interface IBrowserPreviewStatusBadgeProps {
  status: TBrowserPreviewStatus;
}

/**
 * The Live/Replay badge shown next to a browser card's title, matching the
 * expanded view's header badge.
 *
 * @param props - Component props
 * @param props.status - The preview status; renders nothing when null
 * @returns The badge, or null
 */
export function BrowserPreviewStatusBadge({
  status,
}: IBrowserPreviewStatusBadgeProps) {
  const { t } = useTranslation('browser-automation');
  if (!status) return null;
  return (
    <Badge
      variant="secondary"
      className="shrink-0 gap-1.5"
      data-testid="browser-preview-status-badge"
    >
      {status === 'live' && <StatusDot tone="success" pulse />}
      {status === 'live'
        ? t('browserAutomation.action.live')
        : t('browserAutomation.video.replayBadge')}
    </Badge>
  );
}
